import React from 'react';
import { User, CheckCircle, Heart, Users } from 'lucide-react';
import { useDevice } from '@/contexts/DeviceContext';
import { useUserDevice } from '@/hooks/useUserDevice';
import { useUserIdentity } from '@/hooks/useUserIdentity';

const General: React.FC = () => {
  const { selectedDevice } = useDevice();
  const { device, isLoading: isLoadingDevice } = useUserDevice();
  const { identity, supervisorInfo, supervisorLabel, isLoading: isLoadingIdentity } = useUserIdentity();

  const userName = device?.user?.firstName && device?.user?.lastName
    ? `${device.user.firstName} ${device.user.lastName}`
    : device?.user?.firstName || device?.user?.name || selectedDevice?.user?.name || 'kPhone User';

  const showSupervisorSection = (identity === 'child' || identity === 'mentee') && supervisorLabel;

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col pb-24">
      {/* Header */}
      <div className="bg-background border-b border-border flex-shrink-0">
        <div className="px-5 pt-6 pb-4">
          <h1 className="text-[28px] font-bold tracking-tight text-foreground">General</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pt-6 space-y-6">
        {/* User Profile Card */}
        <div className="bg-background rounded-xl overflow-hidden shadow-sm p-5">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h2 className="text-[20px] font-semibold text-foreground">{userName}</h2>
            </div>
          </div>

          {/* Device Registered Status */}
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span className="text-[15px] font-medium">Device Registered</span>
          </div>
        </div>

        {/* Parent/Mentor Section */}
        {showSupervisorSection && (
          <div>
            <h2 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-4 mb-2">
              {supervisorLabel}
            </h2>
            <div className="bg-background rounded-xl overflow-hidden shadow-sm">
              {isLoadingIdentity ? (
                <div className="p-4 text-center text-muted-foreground text-[13px]">
                  Loading...
                </div>
              ) : supervisorInfo ? (
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      identity === 'child' ? 'bg-pink-500/10' : 'bg-blue-500/10'
                    }`}>
                      {identity === 'child' ? (
                        <Heart className="w-5 h-5 text-pink-500" />
                      ) : (
                        <Users className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-[15px] font-medium text-foreground">
                        {supervisorInfo.firstName} {supervisorInfo.lastName}
                      </p>
                      {supervisorInfo.email && (
                        <p className="text-[13px] text-muted-foreground">{supervisorInfo.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground text-[13px]">
                  No {supervisorLabel?.toLowerCase()} assigned
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default General;
